import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Budgets — project-wise budgets AND month-wise overhead caps per cost centre.
 * Actuals + % consumed + colour-coded status pulled from /budgets/status.
 */
export default function Budgets() {
  const [status, setStatus] = useState([]);
  const [projects, setProjects] = useState([]);
  const [ccs, setCCs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createKind, setCreateKind] = useState("project");

  const load = useCallback(async () => {
    const [s, p, c] = await Promise.all([
      api.get("/budgets/status"),
      api.get("/projects"),
      api.get("/cost-centres"),
    ]);
    setStatus(s.data);
    setProjects(p.data);
    setCCs(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const projectRows = useMemo(() => status.filter((r) => r.kind === "project"), [status]);
  const overheadRows = useMemo(() => status.filter((r) => r.kind === "overhead"), [status]);

  const del = async (id) => {
    if (!window.confirm("Delete this budget?")) return;
    try {
      await api.delete(`/budgets/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openCreate = (kind) => {
    setCreateKind(kind);
    setCreating(true);
  };

  const breached = status.filter((r) => r.status === "breach").length;
  const warned = status.filter((r) => r.status === "warn").length;

  return (
    <div data-testid="budgets-page">
      <PageHeader
        title="Budgets"
        module="Finance · Controls"
        subtitle={`${projectRows.length} project · ${overheadRows.length} overhead caps. ${breached ? `⚠ ${breached} breached` : ""} ${warned ? `${warned} approaching` : ""}`}
        ribbon={
          <>
            <BcRibbonGroup label="New">
              <BcRibbonBtn icon={Plus} label="Project Budget" variant="primary" onClick={() => openCreate("project")} testid="ribbon-new-project-budget" />
              <BcRibbonBtn icon={Plus} label="Overhead Cap" onClick={() => openCreate("overhead")} testid="ribbon-new-overhead-budget" />
            </BcRibbonGroup>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="ribbon-refresh" />
            </BcRibbonGroup>
          </>
        }
      />

      <div className="p-6">
        <Tabs defaultValue="project" data-testid="budget-tabs">
          <TabsList>
            <TabsTrigger value="project" data-testid="tab-project-budgets">
              Project Budgets ({projectRows.length})
            </TabsTrigger>
            <TabsTrigger value="overhead" data-testid="tab-overhead-budgets">
              Monthly Overhead Caps ({overheadRows.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="project" className="mt-5">
            {projectRows.length === 0 ? (
              <EmptyState
                title="No project budgets yet"
                description="Set a spend cap per project to catch cost overruns before they hurt the margin."
                action={<Button onClick={() => openCreate("project")} data-testid="empty-new-project-budget" className="bg-[#EA580C] hover:bg-[#c2410c] text-white rounded-sm">New Project Budget</Button>}
              />
            ) : (
              <BudgetTable rows={projectRows} kind="project" onDelete={del} testid="project-budgets-table" />
            )}
          </TabsContent>

          <TabsContent value="overhead" className="mt-5">
            {overheadRows.length === 0 ? (
              <EmptyState
                title="No overhead caps yet"
                description="Cap your monthly spend on Marketing, Admin, Overhead, etc. so overhead never eats the profit."
                action={<Button onClick={() => openCreate("overhead")} data-testid="empty-new-overhead-budget" className="bg-[#EA580C] hover:bg-[#c2410c] text-white rounded-sm">New Overhead Cap</Button>}
              />
            ) : (
              <BudgetTable rows={overheadRows} kind="overhead" onDelete={del} testid="overhead-budgets-table" />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <BudgetCreateSheet
        open={creating}
        onOpenChange={setCreating}
        kind={createKind}
        projects={projects}
        ccs={ccs}
        onCreated={load}
      />
    </div>
  );
}

function BudgetTable({ rows, kind, onDelete, testid }) {
  return (
    <div className="bc-card overflow-hidden" data-testid={testid}>
      <Table className="bc-list">
        <TableHeader>
          <TableRow>
            <TableHead>{kind === "project" ? "Project" : "Cost Centre"}</TableHead>
            {kind === "overhead" && <TableHead>Month</TableHead>}
            <TableHead className="text-right">Budget</TableHead>
            <TableHead className="text-right">Actual</TableHead>
            <TableHead className="text-right">Remaining</TableHead>
            <TableHead>Progress</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid={`budget-row-${r.id}`}>
              <TableCell className="font-semibold text-[#201F1E]">
                {kind === "project" ? (r.project_name || "—") : r.cost_centre_code}
              </TableCell>
              {kind === "overhead" && <TableCell className="font-mono-num text-[#605E5C]">{r.month}</TableCell>}
              <TableCell className="text-right font-mono-num">{fmtMoney(r.amount)}</TableCell>
              <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(r.actual)}</TableCell>
              <TableCell className={`text-right font-mono-num ${r.remaining < 0 ? "text-red-700 font-semibold" : "text-[#323130]"}`}>{fmtMoney(r.remaining)}</TableCell>
              <TableCell><ProgressBar pct={r.pct} status={r.status} /></TableCell>
              <TableCell><StatusPill status={r.status} pct={r.pct} /></TableCell>
              <TableCell>
                <button onClick={() => onDelete(r.id)} data-testid={`delete-budget-${r.id}`} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ProgressBar({ pct, status }) {
  const width = Math.min(100, Math.max(0, pct || 0));
  const color =
    status === "breach" ? "bg-[#A4262C]"
    : status === "warn" ? "bg-[#8A6E00]"
    : "bg-[#107C10]";
  return (
    <div className="w-32 h-2 bg-[#EDEBE9] rounded-[2px] overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${width}%` }} />
    </div>
  );
}

function StatusPill({ status, pct }) {
  const map = {
    breach: { bg: "bg-[#FDE7E9]", text: "text-[#A4262C]", label: "Breach" },
    warn:   { bg: "bg-[#FFF4CE]", text: "text-[#8A6E00]", label: "Approaching" },
    ok:     { bg: "bg-[#DFF6DD]", text: "text-[#107C10]", label: "On track" },
  };
  const s = map[status] || map.ok;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-[2px] text-[10.5px] font-semibold ${s.bg} ${s.text} rounded-[2px]`}>
      {status === "breach" && <Warning size={10} weight="fill" />}
      {s.label} · {pct}%
    </span>
  );
}

function BudgetCreateSheet({ open, onOpenChange, kind, projects, ccs, onCreated }) {
  const [projectId, setProjectId] = useState("");
  const [cc, setCC] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setProjectId(""); setCC(""); setAmount(""); setNote("");
      setMonth(new Date().toISOString().slice(0, 7));
    }
  }, [open]);

  const submit = async () => {
    try {
      const body = {
        kind,
        amount: Number(amount),
        note,
        ...(kind === "project" ? { project_id: projectId } : { cost_centre_code: cc, month }),
      };
      await api.post("/budgets", body);
      toast.success("Budget created");
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[440px]">
        <SheetHeader><SheetTitle className="font-display">New {kind === "project" ? "Project" : "Overhead"} Budget</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-6">
          {kind === "project" ? (
            <div>
              <Label className="label-caps mb-1 block">Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger data-testid="budget-project-select"><SelectValue placeholder="Choose a project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div>
                <Label className="label-caps mb-1 block">Cost Centre</Label>
                <Select value={cc} onValueChange={setCC}>
                  <SelectTrigger data-testid="budget-cc-select"><SelectValue placeholder="Choose a cost centre" /></SelectTrigger>
                  <SelectContent>
                    {ccs.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-caps mb-1 block">Month (YYYY-MM)</Label>
                <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="budget-month-input" />
              </div>
            </>
          )}
          <div>
            <Label className="label-caps mb-1 block">Amount (₹)</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="budget-amount-input" />
          </div>
          <div>
            <Label className="label-caps mb-1 block">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" />
          </div>
          <Button onClick={submit} data-testid="save-budget-button" className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">Save Budget</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
